/**
 * The person colour system.
 *
 * The handoff calls this the entire information architecture, and it is: every
 * event belongs to one or more people, and the colour is how the calendar gets
 * read without reading it. Three rules come with it —
 *
 *   - Person colour means *person*. Never decorative, never a gradient, never
 *     on anything that is not attributed to someone.
 *   - The set is built on cool/warm alternation plus a lightness ladder, so it
 *     survives red-green colour blindness and monochrome.
 *   - Colours are editable in settings, so every variant has to be derived
 *     rather than looked up. The five seeded colours land within a shade of the
 *     hand-tuned values in the handoff.
 */

const PAPER = "#F7F3E9";
const DARK_INK = "#241B05";

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb {
  const clean = hex.replace("#", "");
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const part = (n: number): string =>
    Math.round(Math.max(0, Math.min(255, n)))
      .toString(16)
      .padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

function toHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) return { h: 0, s: 0, l };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;

  return { h, s, l };
}

function fromHsl(h: number, s: number, l: number): Rgb {
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255 };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const channel = (t: number): number => {
    let v = t;
    if (v < 0) v += 1;
    if (v > 1) v -= 1;
    if (v < 1 / 6) return p + (q - p) * 6 * v;
    if (v < 1 / 2) return q;
    if (v < 2 / 3) return p + (q - p) * (2 / 3 - v) * 6;
    return p;
  };

  return { r: channel(h + 1 / 3) * 255, g: channel(h) * 255, b: channel(h - 1 / 3) * 255 };
}

/** WCAG relative luminance. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (v: number): number => {
    const n = v / 255;
    return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: string, b: string): number {
  const la = luminance(parseHex(a));
  const lb = luminance(parseHex(b));
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The faint fill behind an event. Keeps the hue and drops to near-paper
 * lightness, which is why it reads as "this person" rather than "grey box".
 */
export function tintOf(color: string): string {
  const { h, s } = toHsl(parseHex(color));
  return toHex(fromHsl(h, Math.min(s, 0.4), 0.95));
}

/**
 * Text on a filled person colour: whichever of the two available inks is
 * legible. Ochre gets dark ink; everything else gets paper. Derived rather
 * than listed, so a colour someone picks in settings is handled too.
 */
export function foregroundOn(color: string): string {
  return contrast(color, PAPER) >= contrast(color, DARK_INK) ? PAPER : DARK_INK;
}

/** Lifted variant for dark surfaces. Unused until a dark surface exists. */
export function liftedOf(color: string): string {
  const { h, s, l } = toHsl(parseHex(color));
  return toHex(fromHsl(h, s, Math.min(0.72, l + 0.14)));
}

export interface PersonColors {
  color: string;
  tint: string;
  foreground: string;
}

export function colorsFor(color: string): PersonColors {
  return { color, tint: tintOf(color), foreground: foregroundOn(color) };
}

/** First letter, for the person discs. */
export function initialOf(name: string): string {
  return (name.trim()[0] ?? "?").toUpperCase();
}
